import { useState, useCallback, useRef, useEffect } from "react";
import Axios from "axios";
import { environment } from "../../environment";
import { errorCatcher, useSnackbar } from "../../hooks";
import { useLogin } from "../login";
import { IApiAccessRequest, ICreateApiAccessRequest } from "./types";

const baseUrl = `${environment.v2Url}/api-access-requests`;

// #region useApiAccessRequests
export const useApiAccessRequests = () => {
    const [items, setItems] = useState<IApiAccessRequest[]>([]);
    const [loading, setLoading] = useState(false);
    const [, setSnackbar] = useSnackbar();
    const [, authToken, loginFunctions] = useLogin();
    const loginFunctionsRef = useRef(loginFunctions);

    useEffect(() => {
        loginFunctionsRef.current = loginFunctions;
    }, [loginFunctions]);

    const headers = { 'X-Wallmates-Auth': authToken };

    // #region list (super-admin: pending across all brands)
    const list = useCallback(async (): Promise<IApiAccessRequest[] | null> => {
        setLoading(true);
        const result = await Axios.get(baseUrl, { headers })
            .catch((err: any) => errorCatcher(err, loginFunctionsRef.current.logout));
        setLoading(false);
        if (result) {
            setItems(result.data as IApiAccessRequest[]);
            return result.data as IApiAccessRequest[];
        }
        return null;
    }, [authToken]);

    // #region getMine (brand: own pending request or null)
    const getMine = useCallback(async (): Promise<IApiAccessRequest | null> => {
        const result = await Axios.get(`${baseUrl}/mine`, { headers })
            .catch((err: any) => errorCatcher(err, loginFunctionsRef.current.logout));
        return result ? (result.data as IApiAccessRequest | null) : null;
    }, [authToken]);

    // #region create (brand requests access)
    const create = useCallback(async (input: ICreateApiAccessRequest = {}): Promise<IApiAccessRequest | null> => {
        const result = await Axios.post(baseUrl, input, { headers })
            .catch((err: any) => errorCatcher(err, loginFunctionsRef.current.logout));
        if (result) {
            setSnackbar({ show: true, snackbarLevel: 'info', text: 'API access request submitted.' });
            return result.data as IApiAccessRequest;
        }
        return null;
    }, [authToken]);

    // #region approve (super-admin)
    const approve = useCallback(async (id: number): Promise<boolean> => {
        const result = await Axios.post(`${baseUrl}/${id}/approve`, {}, { headers })
            .catch((err: any) => errorCatcher(err, loginFunctionsRef.current.logout));
        if (result) {
            setSnackbar({ show: true, snackbarLevel: 'info', text: 'API access granted.' });
            await list();
            return true;
        }
        return false;
    }, [authToken, list]);

    // #region deny (super-admin)
    const deny = useCallback(async (id: number): Promise<boolean> => {
        const result = await Axios.post(`${baseUrl}/${id}/deny`, {}, { headers })
            .catch((err: any) => errorCatcher(err, loginFunctionsRef.current.logout));
        if (result) {
            setSnackbar({ show: true, snackbarLevel: 'info', text: 'API access request denied.' });
            await list();
            return true;
        }
        return false;
    }, [authToken, list]);

    return { items, loading, list, getMine, create, approve, deny };
};
